<template>
  <s-page-block class="flex flex-col gap-4">
    <s-page-header :heading="t('page.heading')" :description="t('page.description')" />
    <template v-if="!timelimitEnabled">
      <p>{{ t('help.timelimits-not-enabled') }}</p>
      <s-btn
        icon="cog"
        class="w-fit"
        :href="
          route('courses.assignments.settings.edit', {
            course: course.id,
            assignment: assignment.id,
          })
        "
      >
        {{ t('link.go-to-settings') }}
      </s-btn>
    </template>
    <s-placeholder v-else-if="!extensions.data.length">
      <p>
        {{ t('table.empty') }}
      </p>
    </s-placeholder>
    <template v-else>
      <s-data-table
        density="denser"
        :data="extensions.data"
        :columns="[
          {key: 'minutes', label: t('time.minutes')},
          {key: 'timer.user.fullName', label: t('table.header.student')},
          {key: 'grantedBy.fullName', label: t('table.header.grantedBy')},
          {key: 'claimed', label: t('table.header.status')},
          {key: 'actions', label: t('table.header.actions')},
        ]"
      >
        <template #td:claimed="extension">
          <s-badge v-if="extension.claimed" color="green" size="sm">
            {{ t('table.status.claimed') }}
          </s-badge>
          <s-badge v-else color="gray" size="sm">
            {{ t('table.status.unclaimed') }}
          </s-badge>
        </template>

        <template #td:actions="extension">
          <div class="flex gap-2" v-if="!extension.claimed">
            <s-btn
              icon="pencil"
              size="sm"
              :href="
                route('courses.assignments.students.timer_extensions.edit', {
                  course: course.id,
                  assignment: assignment.id,
                  user: extension.timer.user.id,
                  timerExtension: extension.id,
                  _query: {
                    redirect: currentPathAndQuery(),
                  },
                })
              "
            >
              {{ t('actions.edit') }}
            </s-btn>
            <s-btn icon="delete" size="sm" color="red" @click="openDeleteConfirmation(extension)">
              {{ t('actions.delete') }}
            </s-btn>
          </div>
        </template>
      </s-data-table>
      <s-paginator :paginator="extensions" />
    </template>
  </s-page-block>

  <s-modal
    v-model:open="showDeleteModal"
    size="sm"
    :title="t('modal.title')"
    :confirm="{
      label: t('modal.confirm'),
      icon: 'trash-can',
      color: 'red',
    }"
    @confirm="handleDelete"
    @close="showDeleteModal = false"
    cancellable
  >
    <template #content>
      <p>{{ t('modal.content') }}</p>
    </template>
  </s-modal>
</template>
<script setup lang="ts">
import {router} from '@inertiajs/vue3';
import {Course} from '../../types/entities/course';
import {Assignment} from '../../types/entities/assignment';
import SPageBlock from '../../design-system/SPageBlock.vue';
import SPageHeader from '../../design-system/SPageHeader.vue';
import SPaginator from '../../design-system/SPaginator.vue';
import {LaravelPaginator} from '../../types/laravel-paginator';
import {useI18n} from 'vue-i18n';
import SBadge from '../../design-system/SBadge.vue';
import SBtn from '../../design-system/SBtn.vue';
import {route} from 'ziggy-js';
import SPlaceholder from '../../design-system/SPlaceholder.vue';
import SModal from '../../design-system/SModal.vue';
import {ref} from 'vue';
import {currentPathAndQuery} from '../../util/redirect';
import STable from '../../design-system/STable.vue';
import UserAssignmentTimerExtensionDto = App.DTOs.UserAssignmentTimerExtensionDto;
import SDataTable from '../../design-system/SDataTable.vue';

type TimerExtension = UserAssignmentTimerExtensionDto & {
  timer: {user: {id: number; fullName: string}};
  grantedBy: {fullName: string};
};

const props = defineProps<{
  course: Course;
  assignment: Assignment;
  extensions: LaravelPaginator<TimerExtension>;
  timelimitEnabled: boolean;
}>();

const {t} = useI18n({
  inheritLocale: true,
  useScope: 'local',
});

const showDeleteModal = ref(false);
const timerExtensionToDelete = ref<TimerExtension | null>(null);

const openDeleteConfirmation = (extension: any) => {
  timerExtensionToDelete.value = extension;
  showDeleteModal.value = true;
};

const handleDelete = () => {
  if (!timerExtensionToDelete.value) {
    return;
  }

  router.delete(
    route('courses.assignments.students.timer_extensions.destroy', {
      course: props.course.id,
      assignment: props.assignment.id,
      user: timerExtensionToDelete.value.timer.user.id,
      timerExtension: timerExtensionToDelete.value.id,
      _query: {
        redirect: currentPathAndQuery(),
      },
    })
  );
};
</script>
<i18n>
{
  "en": {
    "page.heading": "Timelimit Extensions",
    "page.description": "Grant students extra time from the marking screen.",
    "help.timelimits-not-enabled": "There is no timelimit for this assignment. Visit the assignment settings to add a timelimit.",
    "link.go-to-settings": "Go to Assignment Settings",
    "table.header.student": "Student",
    "table.header.grantedBy": "Granted By",
    "table.header.status": "Status",
    "table.header.actions": "Actions",
    "table.status.claimed": "Claimed",
    "table.status.unclaimed": "Unclaimed",
    "table.empty": "No timelimit extensions have been granted for this assignment.",
    "actions.edit": "Edit",
    "actions.delete": "Delete",
    "modal.title": "Confirm Delete",
    "modal.confirm": "Delete Timer Extension",
    "modal.content": "Are you sure you want to delete this Timer Extension?"
  },
  "fr": {
    "page.heading": "Prolongations de délai",
    "page.description": "Accordez aux étudiants du temps supplémentaire par rapport à l’écran de notation.",
    "help.timelimits-not-enabled": "Il n'y a pas de limite de temps pour ce devoir. Consultez les paramètres du devoir pour ajouter une limite de temps.",
    "link.go-to-settings": "Accéder aux paramètres d'affectation",
    "table.header.student": "Étudiant",
    "table.header.grantedBy": "Accordé par",
    "table.header.status": "Statut",
    "table.header.actions": "Actions",
    "table.status.claimed": "Réclamé",
    "table.status.unclaimed": "Non réclamé",
    "table.empty": "Aucune prolongation de délai n'a été accordée pour cette mission.",
    "actions.edit": "Modifier",
    "actions.delete": "Supprimer",
    "modal.title": "Confirmer la suppression",
    "modal.confirm": "Supprimer la prolongation de temps",
    "modal.content": "Êtes-vous sûr de vouloir supprimer cette prolongation de temps?"
  }
}
</i18n>
